import React, { useCallback, useEffect } from 'react'
import { Form, Formik } from 'formik'
import { useStyles } from './AddEditChannelOffer.styles'
import { isEmpty } from 'lodash'
import { AddEditChannelOfferFooter } from './AddEditChannelOfferFooter/AddEditChannelOfferFooter'
import { AddEditChannelOfferHeader } from '../AddEditChannelOfferComponents/AddEditChannelOfferHeader/AddEditChannelOfferHeader'
import { AddEditChannelOfferForm } from '../AddEditChannelOfferComponents/AddEditChannelOfferForm/AddEditChannelOfferForm'
import { AuthorizationDetails } from '../AddEditChannelOfferComponents/AuthorizationDetails/AuthorizationDetails'
import { useDispatch, useSelector } from '../../../store'
import type { ReceivedResellerAuthorization } from '../../../modules/receivedResellerAuthorization/reducer'
import { Grid } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import type { ChannelPartnerPrivateOfferInCreation } from '../../../modules/channelPartnerPrivateOffer/reducer'
import { PlanType } from '@labrav/react-components'
import { useAccountId } from '@labrav/common'
import { getActiveReceivedResaleAuthorizations } from '../../../modules/receivedResellerAuthorization/action'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'
import {
  clearChannelOfferInCreation,
  getChannelPartnerPrivateOffersInCreation,
  sendChannelOffer,
} from '../../../modules/channelPartnerPrivateOffer/action'
import {
  isNonEmptyNumber,
  ScrollToFieldError,
  useCurrentCloudProvider,
} from '@labrav/common'
import { useSyncFlyoutRouteParams } from '../../../common/utils/hooks/useSyncFlyoutRouteParams'
import type { AddEditChannelOfferProps } from './types'
import { generateChannelPartnerPrivateOfferValidationSchema } from '../../../common/utils/ChannelPartnerPrivateOfferValidation'

export type { AddEditChannelOfferProps } from './types'

export const AddEditChannelOffer: React.FC<AddEditChannelOfferProps> = ({
  externalBaseURL,
}) => {
  useSyncFlyoutRouteParams(!!externalBaseURL)

  const { classes } = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    authorizationId,
    channelOfferId,
  } = useParams<{
    cloud?: string
    accountId?: string
    authorizationId?: string
    channelOfferId?: string
  }>()

  const accountIdFromHook = useAccountId({ product: 'flyout' })
  const selectedCloud = useCurrentCloudProvider()

  const accountId = routeAccountId || accountIdFromHook

  const activeReceivedResellerAuthorizations = useSelector<
    ReceivedResellerAuthorization[]
  >(
    state =>
      state?.receivedResellerAuthorization?.activeReceivedResellerAuthorizations
  )
  useEffect(() => {
    if (isEmpty(activeReceivedResellerAuthorizations) && accountId) {
      dispatch(getActiveReceivedResaleAuthorizations({ partnerId: accountId }))
    }
  }, [accountId])

  const selectedReceivedResellerAuth =
    activeReceivedResellerAuthorizations?.find(
      item => item?.id === authorizationId
    )

  const channelPrivateOffer: ChannelPartnerPrivateOfferInCreation = useSelector(
    state =>
      state.channelPartnerPrivateOffers.channelPartnerPrivateOfferInCreation[
        authorizationId || ''
      ]
  )

  useEffect(() => {
    if (selectedReceivedResellerAuth && accountId) {
      dispatch(
        getChannelPartnerPrivateOffersInCreation({
          resellerAuthId: selectedReceivedResellerAuth?.id,
          channelOfferId,
          partnerId: accountId,
        })
      )
    }
  }, [selectedReceivedResellerAuth, channelOfferId, accountId])

  useEffect(() => {
    return () => {
      selectedReceivedResellerAuth?.id &&
        dispatch(clearChannelOfferInCreation(selectedReceivedResellerAuth?.id))
    }
  }, [selectedReceivedResellerAuth?.id])

  const footerWidth = externalBaseURL ? '100%' : 'calc(100% - 72px)'

  const { timeZone } = useTimeZone()

  const validationSchema = generateChannelPartnerPrivateOfferValidationSchema(
    timeZone || 'UTC',
    selectedReceivedResellerAuth?.productType || PlanType.SAAS_CONTRACT,
    selectedReceivedResellerAuth?.resellerLicenseAgreementFiles,
    selectedReceivedResellerAuth?.discountType,
    selectedReceivedResellerAuth
  )

  const handleNext = (id: string, draft: boolean) => {
    const cloudPath = routeCloud?.toLowerCase() || selectedCloud.toLowerCase()

    const channelOfferPath =
      routeCloud && accountId
        ? `/flyout/${cloudPath}/${accountId}/authorization/${selectedReceivedResellerAuth?.id}/channel-offers/${id}/${draft ? 'edit' : 'detail'}`
        : `/flyout/authorization/${selectedReceivedResellerAuth?.id}/channel-offers/${id}/${draft ? 'edit' : 'detail'}`

    navigate(channelOfferPath)
  }

  const saveChannelOffer = ({
    channelOffer,
    saveAsDraft,
  }: {
    channelOffer: ChannelPartnerPrivateOfferInCreation
    saveAsDraft: boolean
  }) => {
    if (selectedReceivedResellerAuth?.id && accountId)
      dispatch(
        sendChannelOffer({
          channelOffer,
          partnerId: accountId,
          resellerAuthId: selectedReceivedResellerAuth?.id,
          saveAsDraft,
          channelOfferId,
          handleNext,
        })
      )
  }

  return (
    <>
      {!isEmpty(channelPrivateOffer) &&
        !isEmpty(selectedReceivedResellerAuth) && (
          <Formik
            initialValues={channelPrivateOffer}
            validationSchema={validationSchema}
            validateOnMount={true}
            enableReinitialize
            onSubmit={values => {
              const isApplied =
                isNonEmptyNumber(values?.adjustmentPercentageInputValue) &&
                values?.adjustmentPercentageInputValue ===
                  values?.adjustmentPercentage
              if (isApplied) {
                saveChannelOffer({ channelOffer: values, saveAsDraft: false })
              }
            }}
          >
            {formikProps => {
              const handleSubmitChannelOffer = useCallback(async () => {
                await formikProps.submitForm()
              }, [formikProps])

              return (
                <Form>
                  <ScrollToFieldError />
                  <div
                    className={classes.root}
                    data-testid="channel-offer-formik-container"
                  >
                    <Grid container spacing={4}>
                      <Grid
                        size={{
                          sm: 12,
                          md: 8,
                          lg: 8,
                        }}
                      >
                        <AddEditChannelOfferHeader
                          resellerAuthorization={selectedReceivedResellerAuth}
                        />
                        <AddEditChannelOfferForm
                          resellerAuthorization={selectedReceivedResellerAuth}
                        />
                      </Grid>
                      <Grid
                        size={{
                          sm: 12,
                          md: 4,
                          lg: 4,
                        }}
                      >
                        <AuthorizationDetails
                          resellerAuthorization={selectedReceivedResellerAuth}
                        />
                      </Grid>
                    </Grid>
                  </div>
                  <AddEditChannelOfferFooter
                    footerWidth={footerWidth}
                    handleSubmitChannelOffer={handleSubmitChannelOffer}
                    saveChannelOffer={saveChannelOffer}
                  />
                </Form>
              )
            }}
          </Formik>
        )}
    </>
  )
}
